import React from 'react';
import { Trophy, User, Users } from 'lucide-react';
import { catalogStatusLabel } from '@/lib/catalogAvailability';
import type { CatalogAvailability, ClientProfile, Colleague } from '@/types';

interface PersonaCardProps {
  world: {
    id: string;
    name: string;
    age: number;
    diagnosis: string;
    description: string;
    color: string;
    gradient: string;
    icon: React.ReactNode;
    client: ClientProfile | null;
    colleague: Colleague | null;
    completed: number;
    total: number;
    catalogStatus: CatalogAvailability;
  };
  onClick: () => void;
}

const PersonaCard: React.FC<PersonaCardProps> = ({ world, onClick }) => {
  const getWorldIcon = () => {
    if (world.client) {
      return <User className="w-5 h-5" />;
    } else if (world.colleague) {
      return <Users className="w-5 h-5" />;
    } else {
      return <Users className="w-5 h-5" />;
    }
  };

  const isTeaser = world.catalogStatus === 'coming_soon';
  const isDraftPublic = world.catalogStatus === 'draft';

  return (
    <div
      className={`relative overflow-hidden rounded-3xl shadow-lg transition-all duration-300 hover:shadow-xl ${
        isTeaser || isDraftPublic ? 'opacity-95 hover:scale-[1.02]' : 'hover:scale-105'
      } cursor-pointer h-100`}
      onClick={() => onClick()}
    >
      {(isTeaser || isDraftPublic) && (
        <div
          className={`absolute top-4 right-4 z-20 text-xs font-semibold px-3 py-1 rounded-full shadow-md ${
            isDraftPublic ? 'bg-slate-800 text-white' : 'bg-amber-100 text-amber-900 border border-amber-200'
          }`}
        >
          {catalogStatusLabel(world.catalogStatus)}
        </div>
      )}

      <div className={`${world.gradient} p-8 flex flex-col justify-between h-full`}>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="relative">
              <img
                src={world.client?.avatar || world.colleague?.avatar || 'https://images.pexels.com/photos/1462637/pexels-photo-1462637.jpeg?auto=compress&cs=tinysrgb&w=400'}
                alt={world.name}
                className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
              />
              <div className={`absolute -bottom-1 -right-1 p-2 rounded-full bg-gradient-to-r ${world.color} text-white shadow-lg`}>
                {getWorldIcon()}
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-2">{world.name}</h2>
          {world.client ? (
            <div className="text-sm text-slate-600 mb-2">
              {world.age} years old • {world.diagnosis}
            </div>
          ) : world.colleague ? (
            <div className="text-sm text-slate-600 mb-2">
              {world.colleague.title} • {world.colleague.organization}
            </div>
          ) : null}
          <p className="text-slate-600 text-sm leading-relaxed">{world.description}</p>
          {isTeaser && (
            <p className="text-amber-800 text-xs mt-3 font-medium">Preview scenarios — full training opens when this persona launches.</p>
          )}
        </div>

        <div className="relative z-10 mt-auto mb-2">
          <div className="flex items-center justify-between ">
            <div className="flex items-center space-x-2">
              <Trophy className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-slate-700">
                {world.completed}/{world.total} levels
              </span>
            </div>
          </div>

          <div className="w-full bg-white/30 rounded-full h-2 mt-2">
            <div
              className={`h-2 rounded-full bg-gradient-to-r ${world.color} transition-all duration-500`}
              style={{ width: `${world.total ? (world.completed / world.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonaCard;
